import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { APP_NAME } from '../../constants/brand'
import { useAuth } from '../../contexts/AuthContext'
import { createNadDebug } from '../../utils/debugLog'
import { isMetaEmail } from '../../utils/metaEmail'

const buildVersionLog = createNadDebug('dw:build-version:badge')
const COMMIT_SHA_RE = /^[0-9a-f]{7,40}$/i

interface BuildVersionBadgeProps {
  buildVersion?: string
  /** Follows the Settings dialog's visibility even when its portal stays mounted. */
  visible?: boolean
}

interface BuildVersionDisplay {
  displayText: string
  fullText: string
  isKnown: boolean
  labelType?: string
  value?: string
}

export function getBuildVersionDisplay(buildVersion: string | undefined): BuildVersionDisplay {
  const normalized = buildVersion?.trim()

  if (!normalized || normalized === 'unknown') {
    return {
      displayText: 'commit unknown',
      fullText: `${APP_NAME} build version unknown`,
      isKnown: false,
    }
  }

  const isCommitSha = COMMIT_SHA_RE.test(normalized)
  const label = isCommitSha ? 'commit' : 'build'
  const value = isCommitSha ? normalized.slice(0, 12) : normalized.slice(0, 32)

  return {
    displayText: `${label} ${value}`,
    fullText: `${APP_NAME} ${label} ${value}`,
    isKnown: true,
    labelType: label,
    value,
  }
}

export function BuildVersionBadge({
  buildVersion = import.meta.env.VITE_APP_BUILD_VERSION,
  visible = true,
}: BuildVersionBadgeProps) {
  const { user, isAuthenticated, isLoading } = useAuth()
  if (isLoading || !isAuthenticated || !isMetaEmail(user?.email)) return null

  return <VisibleBuildVersionBadge buildVersion={buildVersion} visible={visible} />
}

function VisibleBuildVersionBadge({ buildVersion, visible }: BuildVersionBadgeProps) {
  const display = getBuildVersionDisplay(buildVersion)
  const { t } = useTranslation('workspace')
  const location = useLocation()

  const translatedDisplayText = display.isKnown && display.labelType && display.value
    ? t(`buildVersion.${display.labelType}Label`, { value: display.value })
    : t('buildVersion.commitUnknown')
  const translatedFullText = display.isKnown
    ? t('buildVersion.fullText', { appName: APP_NAME, display: translatedDisplayText })
    : t('buildVersion.buildVersionUnknown', { appName: APP_NAME })

  useEffect(() => {
    buildVersionLog({
      event: visible
        ? 'build-version-visible-in-settings-nav-footer'
        : 'build-version-hidden-from-settings-nav-footer',
      trace_id: 'build-version-settings',
      displayText: display.displayText,
      isKnown: display.isKnown,
      placement: 'settings-nav-footer',
      routePath: location.pathname,
    })
    // Modal may either hide its portal or unmount it on close. Capture both
    // paths so a trace can explain where the label went.
    if (visible) {
      return () => {
        buildVersionLog({
          event: 'build-version-hidden-from-settings-nav-footer',
          trace_id: 'build-version-settings',
          displayText: display.displayText,
          placement: 'settings-nav-footer',
          routePath: location.pathname,
        })
      }
    }
  }, [display.displayText, display.isKnown, location.pathname, visible])

  return (
    <div
      aria-label={translatedFullText}
      className="mb-2 truncate border-t border-border-subtle px-2 pt-3 font-mono text-xs text-muted-foreground"
      data-testid="build-version-badge"
      title={translatedFullText}
    >
      {translatedDisplayText}
    </div>
  )
}
